import { useState, useMemo, useRef, useEffect, useCallback } from "react";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";
import { useToast } from "@/hooks/use-toast";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Progress } from "@/components/ui/progress";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Checkbox } from "@/components/ui/checkbox";
import { ExternalLink, RotateCcw, AlertTriangle, Loader2, Trash2, ChevronDown } from "lucide-react";
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "@/components/ui/collapsible";
import { useExistingPosts, type MatchRow } from "@/hooks/useExistingPosts";
import { QUALITY_FLAGS_LABELS } from "@/constants/wpQualityFlags";
import { WpPostsTable } from "./existing-posts/WpPostsTable";
import { ExistingPostsInfoCard } from "./existing-posts/ExistingPostsInfoCard";
import { ClusterFitReviewPanel } from "./existing-posts/ClusterFitReviewPanel";
import { ActionsPanel } from "./existing-posts/ActionsPanel";
import { StepCard, type StepCardStatus } from "./existing-posts/StepCard";
import {
  ExistingPostsHeader,
  type NavigateTarget,
} from "./existing-posts/ExistingPostsHeader";
import { FloatingActivityLog } from "./existing-posts/FloatingActivityLog";
import { PanelSummary } from "./existing-posts/PanelSummary";
import { GoalPicker, type GoalMode } from "./existing-posts/GoalPicker";
import { RedirectProposalsTable } from "./existing-posts/RedirectProposalsTable";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import type { MatchStatus } from "@/types/thematicMaps";
import type { ClusterFitLabel } from "@/services/wpClusterFitClassifier";
import type { FinalAction } from "@/services/decisionEngine";
import {
  startLogRun,
  endLogRun,
  downloadLogRunAsTxt,
  isLogCollectorActive,
} from "@/services/contentEngine/contentLogger";

interface ExistingPostsPanelProps {
  mapId: string;
  mapName: string;
}

function QualityScoreBadge({
  score,
  recommendation,
}: {
  score?: number | null;
  recommendation?: string | null;
}) {
  if (score === null || score === undefined) {
    return <Badge variant="outline" className="text-xs">Nie oceniono</Badge>;
  }
  const color =
    recommendation === "keep"
      ? "bg-green-500/15 text-green-700 dark:text-green-400 border-green-500/30"
      : recommendation === "borderline"
        ? "bg-yellow-500/15 text-yellow-700 dark:text-yellow-400 border-yellow-500/30"
        : "bg-red-500/15 text-red-700 dark:text-red-400 border-red-500/30";
  return (
    <Badge className={`text-xs ${color} border`}>
      {score}/100
    </Badge>
  );
}

function MatchStatusBadge({ status }: { status: MatchStatus | null | undefined }) {
  const cfg: Record<string, { label: string; color: string }> = {
    unchecked: { label: "Do sprawdzenia", color: "bg-gray-500/15 text-gray-700 dark:text-gray-400" },
    unmatched: { label: "Do generowania", color: "bg-green-500/15 text-green-700 dark:text-green-400" },
    matched_keep: { label: "Zachowaj", color: "bg-blue-500/15 text-blue-700 dark:text-blue-400" },
    matched_refurbish: { label: "Do przebudowy", color: "bg-orange-500/15 text-orange-700 dark:text-orange-400" },
    partial: { label: "Częściowo", color: "bg-purple-500/15 text-purple-700 dark:text-purple-400" },
    skip_manual: { label: "Pomiń ręcznie", color: "bg-red-500/15 text-red-700 dark:text-red-400" },
  };
  const s = status || "unchecked";
  const c = cfg[s] || cfg.unchecked;
  return <Badge className={`text-xs ${c.color}`}>{c.label}</Badge>;
}

type LogType = "info" | "success" | "error" | "stage";
interface LogEntry {
  time: string;
  type: LogType;
  message: string;
  debug?: Record<string, unknown>;
}

export function ExistingPostsPanel({ mapId }: ExistingPostsPanelProps) {
  const { user } = useAuth();
  const { toast } = useToast();
  const {
    summary,
    results,
    history,
    wpPosts,
    sync,
    match,
    extractSro,
    syncSeo,
    auditQuality,
    classifyClusterFit,
    changeClusterFit,
    computeFinalAction,
    acceptFinalAction: acceptFinalActionMut,
    overrideFinalAction: overrideFinalActionMut,
    unlockFinalAction: unlockFinalActionMut,
    bulkAcceptByAction,
    processActions,
    exportJson,
    approveRefurbish,
    bulkApprove,
    autoApprove,
    changeStatus,
    rollback,
    resetDebug,
    getOtherMapsUsingWebsite,
  } = useExistingPosts(mapId);

  // Operations log (wzor z ContentEnginePanel) + contentLogger integration
  const [logs, setLogs] = useState<LogEntry[]>([]);
  const [latestRunId, setLatestRunId] = useState<string | null>(null);

  // Logs collected from mutations onLog callbacks. Render: <FloatingActivityLog />
  // (auto-scroll, expand/collapse, debug entries — wszystko w komponencie floating).
  const addLog = useCallback(
    (type: LogType, message: string, debug?: Record<string, unknown>) => {
      const time = new Date().toLocaleTimeString("pl-PL", {
        hour: "2-digit",
        minute: "2-digit",
        second: "2-digit",
      });
      setLogs((prev) => [...prev.slice(-200), { time, type, message, debug }]);
    },
    [],
  );

  const { data: map } = useQuery({
    queryKey: ["map-website", mapId],
    queryFn: async () => {
      const { data } = await supabase
        .from("thematic_maps")
        // Defensive: zawsze SELECT-uj `id` zeby map.id nie bylo undefined dla
        // konsumentow (gdzieindziej moglo strzelic — patrz fix Redirect Mode bug 2026-06-01).
        .select("id, website_id, wp_posts_synced_at, wp_posts_count, website:websites(id, name, url)")
        .eq("id", mapId)
        .maybeSingle();
      return data as {
        id: string;
        website_id: string | null;
        wp_posts_synced_at: string | null;
        wp_posts_count: number | null;
        website?: { id: string; name: string; url: string } | null;
      } | null;
    },
  });

  const [statusFilter, setStatusFilter] = useState<string>("all");
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [showBulkConfirm, setShowBulkConfirm] = useState(false);
  const [rollbackTarget, setRollbackTarget] = useState<string | null>(null);
  const [showResetDialog, setShowResetDialog] = useState(false);
  const [otherMapsCount, setOtherMapsCount] = useState<number>(0);
  const [showClusterFitWarning, setShowClusterFitWarning] = useState(false);
  const [dopelnijPending, setDopelnijPending] = useState(false);
  const [activeTab, setActiveTab] = useState<string>("step-1");
  // STOP-1: picker celu (3 tryby). Default 'full_audit' = obecne zachowanie 1:1.
  // STOP-2: gates per goalMode (Tabs visibility, write disable, default tab).
  // STOP-9: brama wyboru celu — acceptedGoal=null pokazuje GoalPicker BLOKUJĄCO
  //   dopóki user nie zaakceptuje. Persistance per mapa w localStorage.
  const STORAGE_KEY = `existing-intent-goal:${mapId}`;
  const [acceptedGoal, setAcceptedGoal] = useState<GoalMode | null>(() => {
    if (typeof window === "undefined") return null;
    try {
      const saved = window.localStorage.getItem(STORAGE_KEY);
      if (saved === "full_audit" || saved === "redirect" || saved === "read_only") {
        return saved as GoalMode;
      }
    } catch {
      // localStorage unavailable (private mode, SSR) — fallback do bramy
    }
    return null;
  });
  const [pendingChoice, setPendingChoice] = useState<GoalMode | null>(null);

  // STOP-9: po akceptacji goalMode = acceptedGoal. Przed akceptacją brama blokuje
  // resztę panelu (early return na <GoalPicker/>) — goalMode w branch nie używany.
  const goalMode: GoalMode = acceptedGoal ?? "full_audit";

  // STOP-2: derived flagi per mode + reset selectedIds przy mode switch (R8 mitigation).
  const isRedirectMode = goalMode === "redirect";
  const isReadOnly = goalMode === "read_only";
  const writeDisabled = isReadOnly;

  const handleAcceptGoal = () => {
    if (!pendingChoice) return;
    setAcceptedGoal(pendingChoice);
    try {
      window.localStorage.setItem(STORAGE_KEY, pendingChoice);
    } catch {
      // localStorage unavailable — wybór działa tylko w bieżącej sesji
    }
    setSelectedIds(new Set()); // R8: clear bulk selection
    // Default activeTab per mode
    if (pendingChoice === "read_only") setActiveTab("summary");
    else setActiveTab("step-1");
    setPendingChoice(null);
  };

  const handleResetGoal = () => {
    setAcceptedGoal(null);
    setPendingChoice(null);
    try {
      window.localStorage.removeItem(STORAGE_KEY);
    } catch {
      // noop
    }
    setSelectedIds(new Set());
  };

  const refurbishCandidates = useMemo(() => {
    return (results.data || []).filter((r) => r.match_status === "matched_refurbish");
  }, [results.data]);

  const filteredRows = useMemo(() => {
    let rows = results.data || [];
    if (statusFilter !== "all") rows = rows.filter((r) => (r.match_status || "unchecked") === statusFilter);
    if (searchQuery.trim()) {
      const q = searchQuery.toLowerCase();
      rows = rows.filter((r) => r.title.toLowerCase().includes(q));
    }
    return rows;
  }, [results.data, statusFilter, searchQuery]);

  const toggleSelect = (id: string) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const beginRun = (action: string, extraMeta?: Record<string, unknown>): string => {
    const runId = `existing-${mapId.slice(0, 8)}-${action}-${Date.now()}`;
    startLogRun(runId, { mapId, action, ...extraMeta });
    setLatestRunId(runId);
    return runId;
  };

  const handleSync = () => {
    if (!user?.id) return;
    beginRun("sync", { website: map?.website?.name });
    addLog("info", `▶ Pobieranie wpisów + auto Senuto SEO dla ${map?.website?.name || "domeny"}`);
    sync.mutate(
      { userId: user.id, onLog: addLog },
      {
        onSuccess: (r) => {
          const seoLine = r.seo_sync_failed
            ? `Senuto: padł (${r.seo_sync_error ?? "nieznany błąd"}) — użyj "Sync SEO (Senuto)" ręcznie`
            : r.seo_sync
              ? `Senuto: ${r.seo_sync.posts_with_rankings} wpisów z rankingami, ${r.seo_sync.total_keywords} keys`
              : "Senuto: pominięty";
          addLog("success", `✓ Pobieranie done: fetched=${r.fetched}. ${seoLine}`);
          toast({
            title: "Pobieranie zakończone",
            description: `Pobrano ${r.fetched} wpisów. ${seoLine}. Ocena jakości — krok 4.`,
          });
          endLogRun({ fetched: r.fetched, seo_sync: r.seo_sync });
        },
        onError: (err: Error) => {
          addLog("error", `✗ Sync failed: ${err.message}`);
          toast({ title: "Błąd pobierania", description: err.message, variant: "destructive" });
          endLogRun({ error: err.message });
        },
      },
    );
  };

  const handleMatch = () => {
    beginRun(`match`, { mode: "deep" });
    addLog("info", `▶ Matching (L1 hash → L2 Jaccard → L3 LLM confirm)`);
    match.mutate(
      { triggeredBy: user?.id ?? null, onLog: addLog },
      {
        onSuccess: (r) => {
          addLog(
            "success",
            `✓ Matching done ${(r.duration_ms / 1000).toFixed(1)}s — keep=${r.summary.matched_keep} refurbish=${r.summary.matched_refurbish} partial=${r.summary.partial} unmatched=${r.summary.unmatched}`,
            { run_id: r.run_id, summary: r.summary, llm_cost_usd: r.llm_cost_usd },
          );
          if (r.summary.matched_refurbish > 0) {
            addLog("stage", `  ⚠ ${r.summary.matched_refurbish} kandydatów do przebudowy (${r.summary.matched_refurbish_approved} już zatwierdzonych)`);
          }
          toast({
            title: "Matching zakończony",
            description: `keep=${r.summary.matched_keep} refurbish=${r.summary.matched_refurbish} partial=${r.summary.partial} unmatched=${r.summary.unmatched} (${r.duration_ms}ms)`,
          });
          endLogRun({ summary: r.summary, duration_ms: r.duration_ms, cost_usd: r.llm_cost_usd });
        },
        onError: (err: Error) => {
          addLog("error", `✗ Matching failed: ${err.message}`);
          toast({ title: "Błąd matching", description: err.message, variant: "destructive" });
          endLogRun({ error: err.message });
        },
      },
    );
  };

  const handleSyncSeo = () => {
    if (!map?.website_id) return;
    beginRun("senuto-sync", { website_id: map.website_id });
    addLog("info", `▶ Start Senuto SEO sync (positions, kanibalizacja, traffic estimate)`);
    syncSeo.mutate(
      { websiteId: map.website_id, onLog: addLog },
      {
        onSuccess: (r) => {
          addLog(
            "success",
            `✓ Senuto sync ${(r.duration_ms / 1000).toFixed(1)}s — ${r.posts_with_rankings}/${r.total_posts} z rankingami, ${r.total_keywords} keys, ${r.total_cannibalization_pairs} kanibalizacji`,
            { stats: r },
          );
          toast({
            title: "Senuto SEO sync zakończony",
            description: `${r.posts_with_rankings}/${r.total_posts} wpisów z rankingami, ${r.total_keywords} keys, ${r.total_cannibalization_pairs} kanibalizacje (${(r.duration_ms / 1000).toFixed(1)}s)`,
          });
          endLogRun({ stats: r });
        },
        onError: (err: Error) => {
          addLog("error", `✗ Senuto sync failed: ${err.message}`);
          toast({ title: "Błąd Senuto sync", description: err.message, variant: "destructive" });
          endLogRun({ error: err.message });
        },
      },
    );
  };

  const handleAuditQuality = () => {
    if (!map?.website_id) return;
    beginRun("quality-audit", { website_id: map.website_id });
    addLog("info", `▶ Start audyt jakości: (1) heurystyka 6 reguł → (2) LLM SRO 4 wymiary`);
    auditQuality.mutate(
      { websiteId: map.website_id, onLog: addLog, forceReaudit: false },
      {
        onSuccess: (stats) => {
          addLog(
            "success",
            `✓ Quality audit done ${(stats.duration_ms / 1000).toFixed(1)}s: ${stats.succeeded}/${stats.processed}, cost=$${stats.cost_usd.toFixed(4)} · keep=${stats.distribution.keep} borderline=${stats.distribution.borderline} refurbish=${stats.distribution.refurbish}`,
            { stats },
          );
          toast({
            title: "Audyt jakości zakończony",
            description: `keep=${stats.distribution.keep} borderline=${stats.distribution.borderline} refurbish=${stats.distribution.refurbish} (cost $${stats.cost_usd.toFixed(4)})`,
          });
          endLogRun({ stats });
        },
        onError: (err: Error) => {
          addLog("error", `✗ Quality audit failed: ${err.message}`);
          toast({ title: "Błąd audit", description: err.message, variant: "destructive" });
          endLogRun({ error: err.message });
        },
      },
    );
  };

  const runClusterFit = (opts?: { forceReclassify?: boolean }) => {
    beginRun("cluster-fit", {});
    addLog(
      "info",
      `▶ Start cluster fit classification (LLM gpt-4.1-mini, 3 labele: in_scope/adjacent/off_topic)`,
    );
    classifyClusterFit.mutate(
      { onLog: addLog, forceReclassify: opts?.forceReclassify ?? false },
      {
        onSuccess: (stats) => {
          addLog(
            "success",
            `✓ Cluster fit done ${(stats.duration_ms / 1000).toFixed(1)}s: auto=${stats.auto_matched} llm=${stats.llm_classified} · in_scope=${stats.in_scope} adjacent=${stats.adjacent} off_topic=${stats.off_topic} · cost=$${stats.cost_usd.toFixed(4)}`,
            { stats },
          );
          toast({
            title: "Klasyfikacja scope zakończona",
            description: `in_scope=${stats.in_scope} adjacent=${stats.adjacent} off_topic=${stats.off_topic} (cost $${stats.cost_usd.toFixed(4)})`,
          });
          endLogRun({ stats });
        },
        onError: (err: Error) => {
          addLog("error", `✗ Cluster fit failed: ${err.message}`);
          toast({ title: "Błąd klasyfikacji", description: err.message, variant: "destructive" });
          endLogRun({ error: err.message });
        },
      },
    );
  };

  const handleClassifyClusterFit = async () => {
    if (!map?.website_id) return;
    // Pre-flight: sprawdz czy matcher byl odpalony
    const { count: matchedCount } = await supabase
      .from("thematic_nodes")
      .select("id", { count: "exact", head: true })
      .eq("map_id", mapId)
      .not("matched_wp_post_id", "is", null)
      .in("match_status", ["matched_keep", "matched_refurbish", "partial"]);

    if ((matchedCount ?? 0) === 0) {
      setShowClusterFitWarning(true);
      return;
    }
    runClusterFit();
  };

  const handleDopelnij = async () => {
    if (!map?.website_id) return;
    setDopelnijPending(true);
    beginRun("dopelnij", {});
    addLog("info", "▶ Dopełnianie: SRO extraction + Cluster Fit dla brakujących");
    try {
      // 1. SRO extraction (onlyMissing=true) jesli sa braki
      const posts = wpPosts.data?.posts || [];
      const missingSro = posts.filter((p) => !p.sro_extracted_at).length;
      if (missingSro > 0) {
        addLog("info", `  ▶ SRO extraction dla ${missingSro} wpisów bez SRO`);
        await new Promise<void>((resolve, reject) => {
          extractSro.mutate(
            { websiteId: map.website_id!, onLog: addLog, onlyMissing: true },
            {
              onSuccess: (stats) => {
                addLog("success", `  ✓ SRO extraction: ${stats.succeeded}/${stats.processed}`);
                resolve();
              },
              onError: (err) => reject(err),
            },
          );
        });
      }
      // 2. Cluster fit dla brakujących (forceReclassify=false pomija już sklasyfikowane)
      addLog("info", "  ▶ Cluster fit classification dla brakujących");
      runClusterFit({ forceReclassify: false });
    } catch (err) {
      const msg = err instanceof Error ? err.message : String(err);
      addLog("error", `✗ Dopełnianie failed: ${msg}`);
      toast({ title: "Błąd dopełniania", description: msg, variant: "destructive" });
      endLogRun({ error: msg });
    } finally {
      setDopelnijPending(false);
    }
  };

  // ============================================================
  // M15 Decision Engine handlers
  // ============================================================
  const handleComputeFinalActions = () => {
    if (!map?.website_id) return;
    beginRun("decision-engine", { website_id: map.website_id });
    addLog("info", "▶ Decision Engine: recompute final_action dla wszystkich wpisów");
    computeFinalAction.mutate(
      { websiteId: map.website_id, onLog: addLog },
      {
        onSuccess: (stats) => {
          addLog(
            "success",
            `✓ Decision Engine done ${(stats.duration_ms / 1000).toFixed(1)}s: updated=${stats.updated} locked=${stats.locked_skipped} · keep=${stats.distribution.keep} refurbish=${stats.distribution.refurbish} url_preserve=${stats.distribution.refurbish_url_preserve} keep_outer=${stats.distribution.keep_outer} delete=${stats.distribution.delete} cannib=${stats.distribution.consolidate_301} monitor=${stats.distribution.monitor}`,
            { stats },
          );
          toast({
            title: "Decision Engine zakończony",
            description: `updated=${stats.updated}, locked=${stats.locked_skipped}`,
          });
          endLogRun({ stats });
        },
        onError: (err: Error) => {
          addLog("error", `✗ Decision Engine failed: ${err.message}`);
          toast({
            title: "Błąd Decision Engine",
            description: err.message,
            variant: "destructive",
          });
          endLogRun({ error: err.message });
        },
      },
    );
  };

  const handleAcceptFinalAction = (wpPostId: string) => {
    acceptFinalActionMut.mutate(
      { wpPostId, userId: user?.id ?? null },
      {
        onSuccess: (r) => {
          addLog(
            "success",
            `  ✓ Zaakceptowano: ${r.action}${r.cascaded ? " (cascade: refurbish_approved)" : ""}`,
          );
        },
        onError: (err: Error) => {
          addLog("error", `✗ Accept failed: ${err.message}`);
          toast({ title: "Błąd", description: err.message, variant: "destructive" });
        },
      },
    );
  };

  const handleOverrideFinalAction = (
    wpPostId: string,
    newAction: FinalAction,
    reason?: string,
  ) => {
    overrideFinalActionMut.mutate(
      { wpPostId, newAction, reason, userId: user?.id ?? null },
      {
        onSuccess: (r) => {
          addLog(
            "success",
            `  ✓ Override → ${newAction}${r.cascaded ? " (cascade: refurbish_approved)" : ""}`,
          );
        },
        onError: (err: Error) => {
          toast({ title: "Błąd override", description: err.message, variant: "destructive" });
        },
      },
    );
  };

  const handleUnlockFinalAction = (wpPostId: string) => {
    unlockFinalActionMut.mutate(
      { wpPostId },
      {
        onSuccess: () => addLog("success", "  ✓ Odblokowano — wraca do auto-recompute"),
        onError: (err: Error) => {
          toast({ title: "Błąd unlock", description: err.message, variant: "destructive" });
        },
      },
    );
  };

  // Akcje które maja auto-execute po accept (idą przez processWpActionsForWebsite).
  // Refurbish/refurbish_url_preserve idą osobnym torem (publish_queue przy CE batch),
  // keep/consolidate_301 wymagają ręcznej decyzji.
  const AUTO_EXECUTE_ACTIONS: FinalAction[] = ["delete", "keep_outer", "monitor"];

  const handleBulkAcceptByAction = (action: FinalAction) => {
    if (!map?.website_id) return;
    const websiteId = map.website_id;
    const userId = user?.id ?? null;
    const shouldAutoExecute = AUTO_EXECUTE_ACTIONS.includes(action);

    addLog("info", `▶ Bulk accept: ${action}${shouldAutoExecute ? " + auto-execute" : ""}`);

    bulkAcceptByAction.mutate(
      { websiteId, action, userId },
      {
        onSuccess: (r) => {
          addLog(
            "success",
            `✓ Bulk accept ${action}: approved=${r.approved}, cascaded=${r.cascaded}`,
          );
          toast({
            title: `Zaakceptowano ${r.approved} wpisów`,
            description: r.cascaded > 0 ? `Cascade refurbish: ${r.cascaded}` : undefined,
          });

          // Auto-execute dla delete/keep_outer/monitor — jeden klik = wykonane.
          // Refurbish leci osobno przez publish_queue, więc skip.
          // WAŻNE: wywołujemy zawsze (nie tylko gdy approved>0) — recovery scenariusz:
          // jeśli poprzednie execute się przerwało (timeout/error), kolejny klik
          // accept dostaje approved=0 ale są wpisy locked-but-not-executed do
          // dokończenia. processWpActionsForWebsite (idempotent) wykona tylko
          // brakujące (skip already_processed).
          if (shouldAutoExecute) {
            addLog(
              "info",
              `▶ Auto-execute ${action}${r.approved > 0 ? ` (+${r.approved} nowych accept)` : " (recovery — dokończenie poprzednich)"}`,
            );
            processActions.mutate(
              { websiteId, userId, onLog: addLog },
              {
                onSuccess: (s) => {
                  addLog(
                    "success",
                    `✓ Wykonane ${action}: deleted=${s.deleted} keep_outer=${s.kept_outer} monitor=${s.monitored} skipped=${s.skipped_already_processed} failed=${s.failed}`,
                  );
                  toast({
                    title: "Wykonane",
                    description:
                      action === "delete"
                        ? `${s.deleted} wpisów soft-deleted (WP status=draft)`
                        : `${action} zalogowany dla ${
                            action === "keep_outer" ? s.kept_outer : s.monitored
                          } wpisów`,
                  });
                },
                onError: (err: Error) => {
                  toast({
                    title: "Błąd wykonania",
                    description: err.message,
                    variant: "destructive",
                  });
                },
              },
            );
          }
        },
        onError: (err: Error) => {
          toast({ title: "Błąd bulk accept", description: err.message, variant: "destructive" });
        },
      },
    );
  };

  const handleExportJson = () => {
    beginRun("export-json", {});
    addLog("info", "▶ Eksport danych mapy do JSON (truncated, ~500KB-1MB)");
    exportJson.mutate(
      { onLog: (msg) => addLog("info", msg) },
      {
        onSuccess: (r) => {
          addLog(
            "success",
            `✓ Export: ${r.filename} (${(r.size_bytes / 1024).toFixed(0)} KB, ${r.posts_count} wpisów)`,
          );
          toast({
            title: "Eksport gotowy",
            description: `${r.filename} (${(r.size_bytes / 1024).toFixed(0)} KB)`,
          });
          endLogRun({ export: r });
        },
        onError: (err: Error) => {
          addLog("error", `✗ Export failed: ${err.message}`);
          toast({ title: "Błąd eksportu", description: err.message, variant: "destructive" });
          endLogRun({ error: err.message });
        },
      },
    );
  };

  const handleExtractSro = () => {
    if (!map?.website_id) return;
    beginRun("sro-extraction", { website_id: map.website_id });
    addLog("info", `▶ Start SRO extraction (LLM gpt-4.1-mini)`);
    extractSro.mutate(
      { websiteId: map.website_id, onLog: addLog, onlyMissing: true },
      {
        onSuccess: (stats) => {
          addLog(
            "success",
            `✓ SRO extraction done ${(stats.duration_ms / 1000).toFixed(1)}s: ${stats.succeeded}/${stats.processed}, cost=$${stats.cost_usd.toFixed(4)}`,
            { stats },
          );
          toast({
            title: "SRO extraction zakończony",
            description: `Succeeded: ${stats.succeeded}, failed: ${stats.failed}, cost: $${stats.cost_usd.toFixed(4)}`,
          });
          endLogRun({ stats });
        },
        onError: (err: Error) => {
          addLog("error", `✗ SRO extraction failed: ${err.message}`);
          toast({ title: "Błąd SRO extraction", description: err.message, variant: "destructive" });
          endLogRun({ error: err.message });
        },
      },
    );
  };

  const handleOpenReset = async () => {
    if (!map?.website_id) return;
    const count = await getOtherMapsUsingWebsite(mapId, map.website_id);
    setOtherMapsCount(count);
    setShowResetDialog(true);
  };

  const handleReset = () => {
    if (!map?.website_id) return;
    addLog("info", `▶ Reset debug — mapa, matche, sync meta, wpisy dla ${map.website?.name}`);
    resetDebug.mutate(
      { websiteId: map.website_id },
      {
        onSuccess: () => {
          addLog("success", `✓ Reset zakończony. Uruchom "Pobierz i oceń wpisy" aby pobrać od nowa.`);
          toast({
            title: "Zresetowano",
            description: "Uruchom 'Pobierz i oceń wpisy' aby pobrać od nowa.",
          });
          setShowResetDialog(false);
        },
        onError: (err: Error) => {
          addLog("error", `✗ Reset failed: ${err.message}`);
          toast({ title: "Błąd reset", description: err.message, variant: "destructive" });
        },
      },
    );
  };

  const handleBulkApprove = () => {
    addLog("info", `▶ Bulk approve refurbish: ${selectedIds.size} węzłów`);
    bulkApprove.mutate(
      { nodeIds: Array.from(selectedIds), userId: user?.id ?? null },
      {
        onSuccess: (r) => {
          addLog("success", `✓ Zatwierdzono ${r.approved}, błędów: ${r.errors}`);
          toast({ title: "Zatwierdzono", description: `${r.approved} węzłów, ${r.errors} błędów` });
          setSelectedIds(new Set());
          setShowBulkConfirm(false);
        },
      },
    );
  };

  const handleAutoApprove30 = () => {
    addLog("info", `▶ Auto-approve refurbish dla jakości < 30`);
    autoApprove.mutate(
      { threshold: 30, userId: user?.id ?? null },
      {
        onSuccess: (r) => {
          addLog("success", `✓ Auto-zatwierdzono ${r.approved} węzłów`);
          toast({ title: "Auto-zatwierdzono", description: `${r.approved} węzłów` });
        },
      },
    );
  };

  const handleRollback = (backupId: string) => {
    addLog("info", `▶ Rollback backup ${backupId.slice(0, 8)}`);
    rollback.mutate(
      { backupId, userId: user?.id ?? null },
      {
        onSuccess: (r) => {
          if (r.success) {
            addLog("success", `✓ Przywrócono stary content — ${r.wp_url}`);
            toast({ title: "Przywrócono", description: r.wp_url || "" });
          } else {
            addLog("error", `✗ Rollback failed: ${r.error}`);
            toast({ title: "Błąd rollback", description: r.error, variant: "destructive" });
          }
          setRollbackTarget(null);
        },
      },
    );
  };

  if (!map?.website_id) {
    return (
      <div className="max-w-3xl">
        <Card className="border-orange-500/30 bg-orange-50/50 dark:bg-orange-950/20">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <AlertTriangle className="h-5 w-5 text-orange-600" />
              Brak przypisanej domeny WP
            </CardTitle>
            <CardDescription>
              Ten krok (świadomość istniejących wpisów) wymaga powiązania mapy z domeną WordPress.
              Ustaw <strong>website_id</strong> mapy w kroku <strong>Macro</strong>, aby kontynuować.
            </CardDescription>
          </CardHeader>
        </Card>
      </div>
    );
  }

  const s = summary.data;

  // Derived metrics for ExistingPostsHeader (5-phase progress + next-step CTA)
  const headerPosts = wpPosts.data?.posts || [];
  const headerTotal = headerPosts.length;
  const headerWithSro = headerPosts.filter((p) => p.sro_extracted_at).length;
  const headerAudited = headerPosts.filter((p) => p.quality_audited_at).length;
  const headerClassified = headerPosts.filter((p) => p.cluster_fit_classified_at).length;
  const headerWithRankings = headerPosts.filter(
    (p) => (p.seo_total_keywords ?? 0) > 0,
  ).length;
  const headerWithFa = headerPosts.filter((p) => p.final_action).length;
  const headerLockedActions = headerPosts.filter((p) => p.final_action_locked).length;
  const headerMatchedCount = s
    ? s.matched_keep + s.matched_refurbish + s.partial
    : 0;
  // processedCount: Brak dedykowanego flagi w wp_posts dla "wykonano".
  // Używamy refurbish_approved_by_user na nodach jako proxy dla "rozpoczęto wykonanie refurbish".
  const headerProcessedCount = (results.data || []).filter(
    (r) => r.match_status === "matched_refurbish" && r.refurbish_approved_by_user,
  ).length;
  const isAnyPending =
    sync.isPending ||
    extractSro.isPending ||
    syncSeo.isPending ||
    auditQuality.isPending ||
    classifyClusterFit.isPending ||
    match.isPending ||
    dopelnijPending ||
    computeFinalAction.isPending ||
    processActions.isPending ||
    bulkAcceptByAction.isPending ||
    autoApprove.isPending ||
    bulkApprove.isPending ||
    resetDebug.isPending;

  // Navigation handler — Header CTA powoduje przełączenie tabu + scroll do właściwej sekcji.
  // Identyfikatory sekcji są deklaratywne — mapujemy NavigateTarget na tab + opcjonalny anchor.
  const handleNavigateToStep = (target: NavigateTarget) => {
    if (!target) return;
    if (target.startsWith("tab1")) {
      setActiveTab("step-1");
    } else if (target.startsWith("tab2")) {
      setActiveTab("step-2");
    }
    // Scroll po krótkim opóźnieniu, żeby tab content się zrenderował
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        const stepNumber = (() => {
          if (target === "tab1.step1") return "1";
          if (target === "tab1.step2.sro" || target === "tab1.step2.audit") return "2";
          if (target === "tab1.step3") return "3";
          if (target === "tab1.step4") return "4";
          return null;
        })();
        if (stepNumber) {
          const el = document.querySelector(
            `[data-step-card="${stepNumber}"]`,
          ) as HTMLElement | null;
          if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
        }
      });
    });
  };

  // STOP-9: BRAMA BLOKUJĄCA — gdy brak acceptedGoal (świeże wejście / po "Zmień cel"),
  // pokaż TYLKO GoalPicker. Reszta panelu (header, tabs, stepy) NIE renderuje się.
  if (acceptedGoal === null) {
    return (
      <div className="space-y-6">
        <GoalPicker
          value={pendingChoice}
          onChange={setPendingChoice}
          onAccept={handleAcceptGoal}
          websiteName={map?.website?.name ?? undefined}
        />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* ═══════════════════════════════════════════════════════════════════
          TABS — 3 logiczne fazy + zakładka Weryfikacja
          step-1 "Analiza"           → fetch + SRO + audyt + matching + scope (vertical stepper)
          step-2 "Decyzje & Wykonanie" → Decision Engine + refurbish + execute + history
          summary  "Weryfikacja"     → PanelSummary + danger zone
          ═══════════════════════════════════════════════════════════════════ */}
      {/* STOP-9 (2026-06-01): "Zmień cel" link — czyści localStorage + wraca do bramy. */}
      <div className="flex items-center justify-between gap-3 rounded-md border border-border/40 bg-muted/20 px-3 py-2 text-xs">
        <div className="flex items-center gap-2">
          <span className="text-muted-foreground">Aktywny tryb:</span>
          <span className="font-semibold">
            {goalMode === "full_audit"
              ? "Pełny audyt i decyzje"
              : goalMode === "redirect"
              ? "Redirecty stare → nowe"
              : "Tylko podejrzyj"}
          </span>
        </div>
        <button
          type="button"
          onClick={handleResetGoal}
          className="text-muted-foreground hover:text-foreground underline underline-offset-2 hover:underline-offset-4 transition-all"
          title="Wróć do bramy wyboru celu (wyczyści zapamiętany tryb dla tej mapy)"
        >
          Zmień cel
        </button>
      </div>

      {/* Persistent global header — 5-phase progress + next-step CTA, widoczny zawsze */}
      <ExistingPostsHeader
        websiteName={map.website?.name}
        total={headerTotal}
        withSro={headerWithSro}
        audited={headerAudited}
        classified={headerClassified}
        matchedCount={headerMatchedCount}
        withFa={headerWithFa}
        lockedActions={headerLockedActions}
        processedCount={headerProcessedCount}
        withRankings={headerWithRankings}
        isAnyPending={isAnyPending}
        onNavigateToStep={handleNavigateToStep}
        goalMode={goalMode}
      />

      <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-4">
        <TabsList className={`grid w-full h-auto ${goalMode === "full_audit" ? "grid-cols-3" : "grid-cols-2"}`}>
          <TabsTrigger value="step-1" className="text-xs py-2 flex flex-col gap-0.5">
            <span className="font-bold">1️⃣ Analiza</span>
            <span className="text-[10px] opacity-70 font-normal">
              {(() => {
                const posts = wpPosts.data?.posts || [];
                const total = posts.length;
                const audited = posts.filter((p) => p.quality_audited_at).length;
                const classified = posts.filter((p) => p.cluster_fit_classified_at).length;
                const matched = summary.data
                  ? summary.data.matched_keep + summary.data.matched_refurbish + summary.data.partial
                  : 0;
                if (total === 0) return "0 wpisów";
                return `${total} · ${audited}✓ audyt · ${matched} match · ${classified}✓ scope`;
              })()}
            </span>
          </TabsTrigger>
          {/* STOP-2: TabsTrigger step-2 widoczny TYLKO w trybie full_audit. Redirect/Read-Only — Tab2 ukryty. */}
          {goalMode === "full_audit" && (
            <TabsTrigger value="step-2" className="text-xs py-2 flex flex-col gap-0.5">
              <span className="font-bold">2️⃣ Decyzje & Wykonanie</span>
              <span className="text-[10px] opacity-70 font-normal">
                {(() => {
                  const posts = wpPosts.data?.posts || [];
                  const withFa = posts.filter((p) => p.final_action).length;
                  const locked = posts.filter((p) => p.final_action_locked).length;
                  const refurb = (results.data || []).filter(
                    (r) => r.match_status === "matched_refurbish" && r.refurbish_approved_by_user,
                  ).length;
                  if (withFa === 0) return "—";
                  return `${withFa} decyzji · ${locked}✓ accept · ${refurb} refurb`;
                })()}
              </span>
            </TabsTrigger>
          )}
          <TabsTrigger value="summary" className="text-xs py-2 flex flex-col gap-0.5">
            <span className="font-bold">📊 Weryfikacja</span>
            <span className="text-[10px] opacity-70 font-normal">podsumowanie + reset</span>
          </TabsTrigger>
        </TabsList>

        {/* ══════════════════════════════════════════════════════
            TAB 1 — ANALIZA (vertical stepper z 5 krokami)
            Krok 1: Pobierz wpisy + SEO  (handleSync, opt: handleSyncSeo)
            Krok 2: SRO + Audyt jakości  (handleExtractSro + handleAuditQuality)
            Krok 3: Matching nodów        (handleMatch)
            Krok 4: Klasyfikuj scope LLM (handleClassifyClusterFit, opt: handleDopelnij)
                    LOCKED gdy matchedCount === 0 (auto_matched shortcut wymaga matchingu)
            Krok 5: Cluster Fit Review   (manual override per post, optional)
            ══════════════════════════════════════════════════════ */}
        <TabsContent value="step-1" className="space-y-4 mt-4">
          {/* STOP-2: banner trybu + fieldset disabled dla read_only */}
          {goalMode === "redirect" && (
            <div className="rounded-md border border-amber-500/40 bg-amber-500/10 px-4 py-3 text-sm space-y-1">
              <p className="font-semibold text-amber-700 dark:text-amber-300">🔗 Tryb: Redirecty stare → nowe</p>
              <p className="text-xs text-amber-700/80 dark:text-amber-300/80">
                Pokazujemy tylko krok 1 (Pobierz wpisy). Kroki 2-5 (audyt, matching, scope) ukryte — nie potrzebne dla redirect workflow.
                Propozycje redirectów pojawią się jako osobna sekcja po fetch wpisów (STOP-5 follow-up). Tab "Decyzje" ukryty.
              </p>
            </div>
          )}
          {goalMode === "read_only" && (
            <div className="rounded-md border border-slate-500/40 bg-slate-500/10 px-4 py-3 text-sm space-y-1">
              <p className="font-semibold text-slate-700 dark:text-slate-300">👁 Tryb: Tylko podejrzyj (read-only)</p>
              <p className="text-xs text-slate-700/80 dark:text-slate-300/80">
                Wszystkie write buttons disabled. View tabel + export OK. Tab "Decyzje" ukryty. Default tab: "Weryfikacja".
              </p>
            </div>
          )}
          <fieldset disabled={writeDisabled} className="space-y-4">
          {/* Header sekcji + powiązana domena + help */}
          <Card>
            <CardHeader className="pb-3">
              <div className="flex items-start justify-between gap-4">
                <div className="flex-1 min-w-0">
                  <CardTitle className="text-base">Faza 1: Analiza wpisów + matching + scope</CardTitle>
                  <CardDescription>
                    Domena: <strong>{map.website?.name || "—"}</strong>
                    {map.website?.url && (
                      <>
                        {" "}(
                        <a
                          href={map.website?.url}
                          target="_blank"
                          rel="noreferrer"
                          className="text-primary hover:underline"
                        >
                          {map.website?.url}
                        </a>
                        )
                      </>
                    )}
                    . Wykonaj 5 kroków sekwencyjnie poniżej — każdy używa wyników poprzedniego.
                    Po zakończeniu fazy przejdź do zakładki <strong>2️⃣ Decyzje &amp; Wykonanie</strong>.
                  </CardDescription>
                </div>
                <ExistingPostsInfoCard />
              </div>
            </CardHeader>
          </Card>

          {/* Quick reference — kolejność klikania, defaultowo otwarta */}
          <Collapsible defaultOpen={true}>
            <Card className="border-violet-500/30 bg-violet-500/5">
              <CollapsibleTrigger className="w-full text-left">
                <CardHeader className="hover:bg-violet-500/5 transition-colors cursor-pointer py-3">
                  <CardTitle className="flex items-center justify-between text-base text-violet-700 dark:text-violet-400">
                    <span>📖 Kolejność klikania — co po czym</span>
                    <ChevronDown className="h-4 w-4 text-muted-foreground transition-transform data-[state=open]:rotate-180" />
                  </CardTitle>
                </CardHeader>
              </CollapsibleTrigger>
              <CollapsibleContent>
                <CardContent className="pt-0 space-y-3">
                  <ol className="space-y-1.5 text-xs text-foreground/90 ml-5 list-decimal">
                    <li>
                      <strong>Krok 1: &quot;Pobierz wpisy + SEO&quot;</strong> — fetch z WP REST +
                      auto-Senuto. Czekaj na ✓ Gotowe (~30-60s).
                    </li>
                    <li>
                      <strong>Krok 2 — najpierw &quot;Wyekstrahuj SRO&quot;</strong>, potem
                      <strong> &quot;Audyt jakości&quot;</strong> (DWA przyciski). Audyt MUSI być
                      przed Matchingiem — inaczej match_status zostanie matched_keep i sekcja
                      &quot;Kandydaci do przebudowy&quot; w Tab 2 będzie pusta.
                    </li>
                    <li>
                      <strong>Krok 3: &quot;Matching&quot;</strong> — auto-recompute match_status
                      przełączy zmatchowane refurbish-y na <code>matched_refurbish</code>.
                    </li>
                    <li>
                      <strong>Krok 4: &quot;Klasyfikuj scope&quot;</strong> — odblokuje się po
                      Matchingu. Auto-shortcut dla zmatchowanych → in_scope bez LLM.
                    </li>
                    <li>
                      <strong>Krok 5: Cluster Fit Review</strong> — opcjonalny ręczny override
                      scope. Pokaże się tylko gdy są klasyfikacje.
                    </li>
                    <li>
                      Przejdź do <strong>Tab 2 &quot;Decyzje &amp; Wykonanie&quot;</strong> — tam
                      jest osobna instrukcja krok-po-kroku w help cardzie na górze.
                    </li>
                  </ol>
                  <div className="rounded border border-amber-500/30 bg-amber-500/5 p-2.5 text-xs">
                    <p className="text-amber-700 dark:text-amber-400 leading-snug">
                      <strong>💡 Tip:</strong> Header u góry pokazuje &quot;Następny krok: ...&quot;
                      z przyciskiem <strong>Przejdź</strong> — automatycznie przeniesie Cię do
                      właściwego kroku, możesz zaufać i klikać go w pętli.
                    </p>
                  </div>
                </CardContent>
              </CollapsibleContent>
            </Card>
          </Collapsible>

          {/* Vertical stepper */}
          {(() => {
            const posts = wpPosts.data?.posts || [];
            const total = posts.length;
            const withSro = posts.filter((p) => p.sro_extracted_at).length;
            const audited = posts.filter((p) => p.quality_audited_at).length;
            const withSeo = posts.filter((p) => p.seo_last_sync).length;
            const withRankings = posts.filter((p) => (p.seo_total_keywords ?? 0) > 0).length;
            const classified = posts.filter((p) => p.cluster_fit_classified_at).length;
            const inScope = posts.filter((p) => p.cluster_fit === "in_scope").length;
            const adjacent = posts.filter((p) => p.cluster_fit === "adjacent").length;
            const offTopic = posts.filter((p) => p.cluster_fit === "off_topic").length;
            const notClassified = posts.filter((p) => !p.cluster_fit).length;
            const anyClassified = inScope + adjacent + offTopic > 0;

            const allSro = total > 0 && withSro === total;
            const allAudited = withSro > 0 && audited === withSro;
            const allClassified = withSro > 0 && classified === withSro;
            const seoAutoDone = total > 0 && (withRankings > 0 || withSeo > 0);
            const seoNeeded = total > 0 && !seoAutoDone && !!map.wp_posts_synced_at;

            const matchedCount = summary.data
              ? summary.data.matched_keep +
                summary.data.matched_refurbish +
                summary.data.partial
              : 0;
            const hasMatched = matchedCount > 0;

            // Status per krok
            const krok1Status: StepCardStatus = total > 0 ? "done" : "not_started";
            const krok1Detail = total > 0 ? `${total} wpisów` : undefined;

            let krok2Status: StepCardStatus;
            if (total === 0) krok2Status = "locked";
            else if (allSro && allAudited) krok2Status = "done";
            else if (withSro > 0 || audited > 0) krok2Status = "in_progress";
            else krok2Status = "not_started";
            const krok2Detail =
              total > 0 ? `SRO ${withSro}/${total} · audyt ${audited}/${withSro || 0}` : undefined;

            let krok3Status: StepCardStatus;
            if (total === 0) krok3Status = "locked";
            else if (hasMatched) krok3Status = "done";
            else krok3Status = "not_started";
            const krok3Detail = hasMatched ? `${matchedCount} dopasowań` : undefined;

            let krok4Status: StepCardStatus;
            if (total === 0) krok4Status = "locked";
            else if (!hasMatched) krok4Status = "locked";
            else if (allClassified) krok4Status = "done";
            else if (anyClassified) krok4Status = "in_progress";
            else krok4Status = "not_started";
            const krok4Detail = anyClassified
              ? `${classified}/${total}`
              : undefined;

            const krok5Status: StepCardStatus = "optional";

            return (
              <div className="space-y-3">
                {/* KROK 1: Pobierz wpisy + SEO */}
                <StepCard
                  stepNumber={1}
                  title="Pobierz wpisy + SEO"
                  description="Fetch WP REST → wp_posts (structural metrics) + AUTO Senuto SEO (pozycje, traffic, kanibalizacja). Bez oceny jakości — to Krok 2."
                  status={krok1Status}
                  statusDetail={krok1Detail}
                >
                  <div className="flex flex-wrap items-center gap-2">
                    <Button
                      onClick={handleSync}
                      disabled={sync.isPending}
                      variant={total === 0 ? "default" : "secondary"}
                      title="Pobiera wszystkie wpisy + auto-Senuto sync"
                    >
                      {sync.isPending
                        ? "Pobieranie + SEO..."
                        : map.wp_posts_synced_at
                          ? "Odśwież wpisy + SEO"
                          : "Pobierz wpisy + SEO"}
                    </Button>
                    {map.wp_posts_synced_at && (
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={handleSyncSeo}
                        disabled={syncSeo.isPending || total === 0}
                        title={
                          seoAutoDone
                            ? `Auto-sync w głównym przycisku zadziałał (${withRankings} z rankingami). Klik tylko żeby odświeżyć.`
                            : seoNeeded
                              ? "Senuto auto-sync padł — uruchom ręcznie"
                              : "Sync Senuto: pozycje, traffic estimate, kanibalizacja"
                        }
                      >
                        {syncSeo.isPending
                          ? "Senuto..."
                          : seoNeeded
                            ? "⚠ Senuto fallback (wymagane)"
                            : "Senuto fallback"}
                      </Button>
                    )}
                  </div>
                  {total > 0 && (
                    <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 mt-3 text-xs">
                      <div className="rounded border border-border/40 p-2">
                        <p className="text-muted-foreground">Wpisów</p>
                        <p className="text-lg font-bold">{total}</p>
                      </div>
                      <div className="rounded border border-border/40 p-2">
                        <p className="text-muted-foreground">Z rankingami</p>
                        <p className="text-lg font-bold text-emerald-500">{withRankings}</p>
                      </div>
                      <div className="rounded border border-border/40 p-2">
                        <p className="text-muted-foreground">Wysokiej jakości</p>
                        <p className="text-lg font-bold text-emerald-500">
                          {s?.quality_breakdown?.keep ?? 0}
                        </p>
                      </div>
                      <div className="rounded border border-border/40 p-2">
                        <p className="text-muted-foreground">Do przebudowy</p>
                        <p className="text-lg font-bold text-orange-500">
                          {s?.quality_breakdown?.refurbish ?? 0}
                        </p>
                      </div>
                    </div>
                  )}
                  {map.wp_posts_synced_at && (
                    <p className="mt-2 text-[11px] text-muted-foreground">
                      Ostatni sync: {new Date(map.wp_posts_synced_at).toLocaleString("pl-PL")}
                    </p>
                  )}
                </StepCard>

                {/* STOP-2: KROKI 2-5 (SRO/audyt/matching/scope) ukryte w redirect mode — nie potrzebne dla redirect workflow */}
                {!isRedirectMode && (
                <>
                {/* KROK 2: SRO + Audyt jakości */}
                <StepCard
                  stepNumber={2}
                  title="SRO + Audyt jakości"
                  description="(a) LLM extraction entity/attribute/query_frame z gpt-4.1-mini (~$0.001/wpis); (b) heurystyka 6 reguł HTML (instant) + LLM SRO 4 wymiary — entity anchoring, information gain, humanization risk, retrieval ready (~$0.0025/wpis)."
                  status={krok2Status}
                  statusDetail={krok2Detail}
                  lockedReason="Najpierw pobierz wpisy (Krok 1)"
                >
                  <div className="flex flex-wrap items-center gap-2">
                    <Button
                      onClick={handleExtractSro}
                      disabled={extractSro.isPending || total === 0 || allSro}
                      variant={!allSro ? "default" : "secondary"}
                      title={
                        total === 0
                          ? "Najpierw pobierz wpisy (Krok 1)"
                          : allSro
                            ? "Wszystkie wpisy mają już SRO ✓"
                            : "Wypełnij entity/attribute/query_frame przez LLM (gpt-4.1-mini)"
                      }
                    >
                      {extractSro.isPending
                        ? "Ekstrakcja SRO..."
                        : allSro
                          ? "✓ SRO"
                          : "Wyekstrahuj SRO (LLM)"}
                    </Button>
                    <Button
                      onClick={handleAuditQuality}
                      disabled={auditQuality.isPending || withSro === 0 || allAudited}
                      variant={allSro && !allAudited ? "default" : "secondary"}
                      title={
                        withSro === 0
                          ? "Najpierw wyekstrahuj SRO — audyt używa extracted_entity/attribute"
                          : allAudited
                            ? "Wszystkie wpisy z SRO zostały zauditowane ✓"
                            : "Heurystyka + LLM SRO 4-wymiarowy audit"
                      }
                    >
                      {auditQuality.isPending
                        ? "Audyt..."
                        : allAudited
                          ? "✓ Audyt"
                          : "Audyt jakości"}
                    </Button>
                  </div>
                  {total > 0 && (
                    <div className="grid grid-cols-2 gap-2 mt-3 text-xs">
                      <div className="rounded border border-border/40 p-2">
                        <p className="text-muted-foreground">SRO ekstraktowane</p>
                        <p className="font-mono text-foreground">
                          {withSro}/{total}
                        </p>
                      </div>
                      <div className="rounded border border-border/40 p-2">
                        <p className="text-muted-foreground">Audyt LLM</p>
                        <p className="font-mono text-foreground">
                          {audited}/{withSro || 0}
                        </p>
                      </div>
                    </div>
                  )}
                </StepCard>

                {/* KROK 3: Matching */}
                <StepCard
                  stepNumber={3}
                  title="Matching nodów"
                  description="3-poziomowy pipeline: L1 hash title (exact match, 0 false positives) → L2 Jaccard tokenów + entity overlap (containment 0.70 auto / 0.40 borderline) → L3 LLM confirm dla weak matches. Po sukcesie auto-recompute cluster_fit (matched → in_scope shortcut)."
                  status={krok3Status}
                  statusDetail={krok3Detail}
                  lockedReason="Najpierw pobierz wpisy (Krok 1)"
                  hint="Po matchingu Krok 4 (Klasyfikuj scope) wykorzysta auto_matched shortcut — szybciej i taniej (~4× redukcja kosztu LLM)."
                >
                  <Button
                    onClick={handleMatch}
                    disabled={match.isPending || !map.wp_posts_synced_at}
                    variant={!hasMatched && total > 0 ? "default" : "secondary"}
                  >
                    {match.isPending ? "Matching..." : hasMatched ? "Re-matching" : "Matching"}
                  </Button>
                  {s && total > 0 && (
                    <div className="grid grid-cols-3 sm:grid-cols-6 gap-2 mt-3 text-xs">
                      <div className="rounded border border-border/40 p-2">
                        <p className="text-muted-foreground">Do sprawdzenia</p>
                        <p className="font-bold">{s.unchecked}</p>
                      </div>
                      <div className="rounded border border-border/40 p-2">
                        <p className="text-muted-foreground">Do generowania</p>
                        <p className="font-bold text-green-500">{s.unmatched}</p>
                      </div>
                      <div className="rounded border border-border/40 p-2">
                        <p className="text-muted-foreground">Zachowaj</p>
                        <p className="font-bold text-blue-500">{s.matched_keep}</p>
                      </div>
                      <div className="rounded border border-border/40 p-2">
                        <p className="text-muted-foreground">Do przebudowy</p>
                        <p className="font-bold text-orange-500">{s.matched_refurbish}</p>
                      </div>
                      <div className="rounded border border-border/40 p-2">
                        <p className="text-muted-foreground">Częściowe</p>
                        <p className="font-bold text-purple-500">{s.partial}</p>
                      </div>
                      <div className="rounded border border-border/40 p-2">
                        <p className="text-muted-foreground">Pomiń ręcznie</p>
                        <p className="font-bold text-red-500">{s.skip_manual}</p>
                      </div>
                    </div>
                  )}
                </StepCard>

                {/* KROK 4: Klasyfikuj scope (LOCKED gdy brak matchu) */}
                <StepCard
                  stepNumber={4}
                  title="Klasyfikuj scope (LLM)"
                  description="Cluster Fit klasyfikator (in_scope / adjacent / off_topic). Zmatchowane wpisy auto-promote do in_scope BEZ wywołania LLM (oszczędność). Niezmatchowane → LLM call (~$0.005/wpis)."
                  status={krok4Status}
                  statusDetail={krok4Detail}
                  lockedReason="Najpierw odpal Matching (Krok 3) — bez matchingu wszystkie wpisy idą przez LLM (~4× droższe, brak auto_matched shortcut)"
                >
                  <div className="flex flex-wrap items-center gap-2">
                    <Button
                      onClick={handleClassifyClusterFit}
                      disabled={classifyClusterFit.isPending || withSro === 0}
                      variant={hasMatched && !allClassified ? "default" : "secondary"}
                      title={
                        withSro === 0
                          ? "Najpierw wyekstrahuj SRO (Krok 2)"
                          : allClassified
                            ? "Wszystkie wpisy sklasyfikowane ✓"
                            : "Klasyfikacja scope LLM (in_scope/adjacent/off_topic)"
                      }
                    >
                      {classifyClusterFit.isPending
                        ? "Klasyfikacja..."
                        : allClassified
                          ? "✓ Scope"
                          : "Klasyfikuj scope"}
                    </Button>
                    {notClassified > 0 && anyClassified && (
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={handleDopelnij}
                        disabled={
                          dopelnijPending ||
                          extractSro.isPending ||
                          classifyClusterFit.isPending
                        }
                        title="Uzupełni SRO + Cluster Fit dla brakujących wpisów"
                      >
                        {dopelnijPending ? "Dopełnianie..." : `Dopełnij (${notClassified})`}
                      </Button>
                    )}
                  </div>
                  {anyClassified && (
                    <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 mt-3 text-xs">
                      <div className="rounded border border-emerald-500/30 bg-emerald-500/5 p-2">
                        <p className="text-emerald-600 dark:text-emerald-400">in_scope</p>
                        <p className="text-lg font-bold text-emerald-600 dark:text-emerald-400">
                          {inScope}
                        </p>
                      </div>
                      <div className="rounded border border-amber-500/30 bg-amber-500/5 p-2">
                        <p className="text-amber-600 dark:text-amber-400">adjacent</p>
                        <p className="text-lg font-bold text-amber-600 dark:text-amber-400">
                          {adjacent}
                        </p>
                      </div>
                      <div className="rounded border border-red-500/30 bg-red-500/5 p-2">
                        <p className="text-red-600 dark:text-red-400">off_topic</p>
                        <p className="text-lg font-bold text-red-600 dark:text-red-400">
                          {offTopic}
                        </p>
                      </div>
                      <div className="rounded border border-zinc-500/30 bg-zinc-500/5 p-2">
                        <p className="text-zinc-500">nie sklasyfikowane</p>
                        <p className="text-lg font-bold text-zinc-500">{notClassified}</p>
                      </div>
                    </div>
                  )}
                </StepCard>

                {/* KROK 5: Cluster Fit Review (Optional) */}
                {anyClassified && (
                  <StepCard
                    stepNumber={5}
                    title="Cluster Fit Review (manualny override)"
                    description="Przegląd klasyfikacji LLM per wpis w 3 collapsible bucketach (in_scope / adjacent / off_topic). Możesz ręcznie zmienić scope wybranego wpisu (manual override)."
                    status={krok5Status}
                  >
                    <ClusterFitReviewPanel
                      posts={posts}
                      onChangeClusterFit={(opts) =>
                        changeClusterFit.mutate(opts, {
                          onSuccess: () =>
                            toast({
                              title: "Scope zaktualizowany",
                              description: `${opts.newValue} · manual override`,
                            }),
                          onError: (err: Error) =>
                            toast({
                              title: "Błąd zmiany scope",
                              description: err.message,
                              variant: "destructive",
                            }),
                        })
                      }
                      isPending={changeClusterFit.isPending}
                    />
                  </StepCard>
                )}
                </>
                )}
                {/* STOP-2: end isRedirectMode wrapper (kroki 2-5 ukryte w redirect mode) */}
              </div>
            );
          })()}

          {/* Global progress (any operation pending) */}
          {(sync.isPending ||
            extractSro.isPending ||
            syncSeo.isPending ||
            auditQuality.isPending ||
            classifyClusterFit.isPending ||
            match.isPending ||
            dopelnijPending) && <Progress value={undefined} />}

          {/* Lista pobranych wpisów (zwijana) */}
          <WpPostsTable posts={wpPosts.data?.posts || []} loading={wpPosts.isLoading} />
          </fieldset>
          {/* STOP-2: end writeDisabled fieldset wrapper (read_only mode disable all writes) */}

          {/* STOP-5: Redirect Mode UI — tylko w trybie 'redirect' (poniżej Step 1).
              FIX 2026-06-01: pass mapId/websiteId osobno (NIE map.id, ktore moglo byc
              undefined gdy SELECT w map-website useQuery nie zawieral 'id'). */}
          {isRedirectMode && mapId && (
            <RedirectProposalsTable mapId={mapId} websiteId={map?.website_id ?? null} />
          )}
        </TabsContent>
        {/* ══════════════════════════════════════════════════════
            TAB 2 — DECYZJE & WYKONANIE
            Sekcje: (a) Decision Engine — 7 akcji w 3 priorytetach
                    (b) Refurbish kandydaci — bulk approve nodów
                    (c) Wyniki matchingu — pełna tabela z filtrami
                    (d) Historia przebudowy — z opcją rollback
            ══════════════════════════════════════════════════════ */}
        {/* STOP-2: TabsContent step-2 renderowany TYLKO w trybie full_audit. Redirect/Read-Only → tab ukryty. */}
        {goalMode === "full_audit" && (
        <TabsContent value="step-2" className="space-y-4 mt-4">
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-base">
                Faza 2: Decision Engine + wykonanie zatwierdzonych akcji
              </CardTitle>
              <CardDescription>
                Silnik deterministyczny (TS, zero LLM) łączy Jakość + SEO + Scope + Kanibalizację
                w jedną z 7 akcji: <strong>keep / refurbish / refurbish_url_preserve / keep_outer
                / delete / consolidate_301 / monitor</strong>. Po zaakceptowaniu (lock) kliknij
                <strong> ⚡ Wykonaj</strong> — refurbish trafia do Content Engine, delete →
                WP status=draft (rollback-able), keep_outer/monitor → tylko log.
              </CardDescription>
            </CardHeader>
          </Card>

          {/* HIGH priority banner — gdy są pending akcje wpływające na SEO equity */}
          {(() => {
            const posts = wpPosts.data?.posts || [];
            const consolidateCount = posts.filter(
              (p) => p.final_action === "consolidate_301" && !p.final_action_locked,
            ).length;
            const urlPreserveCount = posts.filter(
              (p) => p.final_action === "refurbish_url_preserve" && !p.final_action_locked,
            ).length;
            const totalHighPriority = consolidateCount + urlPreserveCount;
            if (totalHighPriority === 0) return null;
            return (
              <Card className="border-amber-500/40 bg-amber-50/40 dark:bg-amber-950/15">
                <CardHeader className="py-3">
                  <CardTitle className="flex items-center gap-2 text-amber-700 dark:text-amber-400 text-base">
                    <AlertTriangle className="h-5 w-5" />
                    {totalHighPriority} {totalHighPriority === 1 ? "akcja wymaga" : "akcje wymagają"}{" "}
                    uwagi (HIGH priority)
                  </CardTitle>
                  <CardDescription>
                    Te akcje wpływają na SEO equity (URL/redirect) — przejrzyj indywidualnie zanim
                    zaakceptujesz bulk:
                    {consolidateCount > 0 && (
                      <span className="block mt-1">
                        • <strong>consolidate_301</strong>: {consolidateCount} (kanibalizacja —
                        merge z 301 redirect, wymaga decyzji winner/loser)
                      </span>
                    )}
                    {urlPreserveCount > 0 && (
                      <span className="block mt-1">
                        • <strong>refurbish_url_preserve</strong>: {urlPreserveCount}{" "}
                        (refurbish z zachowaniem URL — wpisy z wysokim ruchem SEO)
                      </span>
                    )}
                  </CardDescription>
                </CardHeader>
              </Card>
            );
          })()}

      {/* Help card — instrukcja krok-po-kroku co kliknąć w Decision Engine.
          Defaultowo otwarta przy pierwszym wejściu (lokalny localStorage opt-out). */}
      {map.website_id && (wpPosts.data?.posts || []).length > 0 && (
        <Collapsible defaultOpen={true}>
          <Card className="border-blue-500/30 bg-blue-500/5">
            <CollapsibleTrigger className="w-full text-left">
              <CardHeader className="hover:bg-blue-500/5 transition-colors cursor-pointer py-3">
                <CardTitle className="flex items-center justify-between text-base text-blue-700 dark:text-blue-400">
                  <span>📖 Jak korzystać z Decision Engine — krok po kroku</span>
                  <ChevronDown className="h-4 w-4 text-muted-foreground transition-transform data-[state=open]:rotate-180" />
                </CardTitle>
              </CardHeader>
            </CollapsibleTrigger>
            <CollapsibleContent>
              <CardContent className="space-y-4 pt-0">
                <div>
                  <p className="text-xs font-semibold uppercase tracking-wider text-muted-foreground mb-2">
                    1. Co znaczy każda akcja
                  </p>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 text-xs">
                    <div className="rounded border border-orange-500/30 bg-orange-500/5 p-2.5">
                      <p className="font-semibold text-orange-700 dark:text-orange-400 mb-1">
                        ✏️ Przepisz (refurbish / refurbish_url_preserve)
                      </p>
                      <p className="text-muted-foreground leading-snug">
                        Content Engine wygeneruje NOWY content na ten sam URL. Stara wersja → backup
                        (rollback per wpis dostępny). 🟢 Niskie ryzyko.
                      </p>
                    </div>
                    <div className="rounded border border-red-500/30 bg-red-500/5 p-2.5">
                      <p className="font-semibold text-red-700 dark:text-red-400 mb-1">
                        🗑 Usuń (delete)
                      </p>
                      <p className="text-muted-foreground leading-snug">
                        WP post → status=draft (ukryty ale NIE skasowany, hard delete za 30 dni).
                        🟡 Sprawdź czy wpis nie rankuje przed bulk akceptem.
                      </p>
                    </div>
                    <div className="rounded border border-blue-500/30 bg-blue-500/5 p-2.5">
                      <p className="font-semibold text-blue-700 dark:text-blue-400 mb-1">
                        🌳 Outer cluster (keep_outer)
                      </p>
                      <p className="text-muted-foreground leading-snug">
                        Trzymamy jako outer cluster — nie ruszamy. Tylko log w wp_action_logs.
                        🟢 Zero ryzyka.
                      </p>
                    </div>
                    <div className="rounded border border-zinc-500/30 bg-zinc-500/5 p-2.5">
                      <p className="font-semibold text-zinc-600 dark:text-zinc-400 mb-1">
                        👁 Monitor (ręczna)
                      </p>
                      <p className="text-muted-foreground leading-snug">
                        Watch and wait — edge case wymaga ręcznej decyzji. Tylko log.
                        🟢 Zero ryzyka.
                      </p>
                    </div>
                    <div className="rounded border border-purple-500/30 bg-purple-500/5 p-2.5 sm:col-span-2">
                      <p className="font-semibold text-purple-700 dark:text-purple-400 mb-1">
                        🔀 Consolidate 301 (consolidate_301)
                      </p>
                      <p className="text-muted-foreground leading-snug">
                        Wykryta kanibalizacja — merge z partner URL + 301 redirect. Wymaga ręcznej
                        decyzji winner/loser. 🟡 Wpływa na SEO equity — przejrzyj indywidualnie.
                      </p>
                    </div>
                  </div>
                </div>

                <div>
                  <p className="text-xs font-semibold uppercase tracking-wider text-muted-foreground mb-2">
                    2. Kolejność klikania (od najbezpieczniejszych do ryzykownych)
                  </p>
                  <ol className="space-y-1.5 text-xs text-foreground/90 ml-5 list-decimal">
                    <li>
                      <strong>Kliknij &quot;Przelicz akcje&quot;</strong> (jeśli jeszcze nie
                      kliknąłeś) — Decision Engine policzy final_action dla każdego wpisu.
                    </li>
                    <li>
                      <strong>Rozwiń każdą grupę</strong> (strzałka {">"} z lewej) — przejrzyj wpisy
                      i reason dlaczego ta akcja. <strong>Krytycznie: rozwiń &quot;Usuń&quot;</strong>
                      i sprawdź czy żaden z wpisów nie ma rankingów Senuto.
                    </li>
                    <li>
                      <strong>Outer cluster → &quot;Zaakceptuj wszystkie&quot;</strong> (zero ryzyka)
                    </li>
                    <li>
                      <strong>Monitor → &quot;Zaakceptuj wszystkie&quot;</strong> (zero ryzyka)
                    </li>
                    <li>
                      <strong>Przepisz → &quot;Zaakceptuj wszystkie&quot;</strong> (niskie ryzyko —
                      kaskaduje refurbish_approved_by_user=true na zmatchowane nody, sekcja
                      &quot;Kandydaci do przebudowy&quot; sama się przełączy)
                    </li>
                    <li>
                      <strong>Usuń → po przejrzeniu</strong> klikasz
                      &quot;Zaakceptuj wszystkie&quot; (lub indywidualnie per wpis przez Override)
                    </li>
                    <li>
                      <strong>⚡ Wykonaj zatwierdzone akcje</strong> (fioletowy przycisk u góry)
                      — TO JEDYNY krok który fizycznie ruszy WordPress
                    </li>
                  </ol>
                </div>

                <div className="rounded border border-amber-500/30 bg-amber-500/5 p-3 text-xs">
                  <p className="font-semibold text-amber-700 dark:text-amber-400 mb-1">
                    ⚠ Pamiętaj
                  </p>
                  <ul className="space-y-1 text-muted-foreground leading-snug list-disc ml-5">
                    <li>
                      Kliknięcie <strong>&quot;Zaakceptuj wszystkie&quot;</strong> w grupie =
                      tylko ustawia <code>final_action_locked=true</code> w bazie. WP nietknięty.
                    </li>
                    <li>
                      Dopiero <strong>&quot;⚡ Wykonaj zatwierdzone akcje&quot;</strong> faktycznie
                      idzie na WP (delete → draft, refurbish → CE generuje nowy content przez
                      publish_queue, keep_outer/monitor → tylko log).
                    </li>
                    <li>
                      Wszystko jest <strong>reversible</strong> — soft delete można cofnąć z WP
                      Admin (status=draft → publish), refurbish ma rollback per wpis.
                    </li>
                    <li>
                      Możesz pominąć <strong>&quot;Kandydaci do przebudowy&quot;</strong> jeśli
                      zaakceptowałeś Przepisz w Decision Engine (kaskada robi to za Ciebie).
                    </li>
                  </ul>
                </div>
              </CardContent>
            </CollapsibleContent>
          </Card>
        </Collapsible>
      )}

      {/* M15: ActionsPanel (Decision Engine) — spina 4 wymiary w 7 akcji */}
      {map.website_id && (wpPosts.data?.posts || []).length > 0 && (
        <ActionsPanel
          posts={wpPosts.data?.posts || []}
          websiteId={map.website_id}
          userId={user?.id}
          onAccept={handleAcceptFinalAction}
          onOverride={handleOverrideFinalAction}
          onUnlock={handleUnlockFinalAction}
          onBulkAccept={handleBulkAcceptByAction}
          onCompute={handleComputeFinalActions}
          isComputing={computeFinalAction.isPending}
        />
      )}

      {/* Section 5: Refurbish candidates (legacy node-level approve view).
          Renderuje się TYLKO gdy są nody z match_status='matched_refurbish'.
          W większości przypadków pusta, bo Decision Engine + bulk accept w grupie
          "Przepisz" robi kaskadę refurbish_approved=true automatycznie.
          Pokazuje się tylko gdy user chce per-node manual approve (rzadkie). */}
      {refurbishCandidates.length > 0 && (
        <Card className="border-orange-500/30 bg-orange-50/30 dark:bg-orange-950/10">
          <CardHeader>
            <CardTitle>Kandydaci do przebudowy (per-node)</CardTitle>
            <CardDescription>
              Nody z <code>match_status=matched_refurbish</code> — alternatywny widok per node.
              Większość przypadków obsługuje grupa <strong>Przepisz</strong> w Decision Engine
              wyżej (kaskaduje approval automatycznie).
            </CardDescription>
          </CardHeader>
          <CardContent>
            <>
              <div className="flex items-center justify-between mb-3">
                <p className="text-sm">
                  {refurbishCandidates.length} kandydatów,{" "}
                  {refurbishCandidates.filter((r) => r.refurbish_approved_by_user).length} zatwierdzonych
                </p>
                <div className="flex gap-2">
                  <Button variant="outline" size="sm" onClick={handleAutoApprove30} disabled={autoApprove.isPending}>
                    Zatwierdź wszystkie z jakością &lt; 30
                  </Button>
                  <Button
                    size="sm"
                    onClick={() => setShowBulkConfirm(true)}
                    disabled={selectedIds.size === 0 || bulkApprove.isPending}
                  >
                    Zatwierdź zaznaczone ({selectedIds.size})
                  </Button>
                </div>
              </div>
              <div className="rounded border bg-background">
                <table className="w-full text-sm">
                  <thead className="bg-muted/50">
                    <tr>
                      <th className="w-8 p-2"></th>
                      <th className="text-left p-2">Node</th>
                      <th className="text-left p-2">Istniejący wpis</th>
                      <th className="text-left p-2">Jakość</th>
                      <th className="text-left p-2">Problemy</th>
                      <th className="text-left p-2">Status</th>
                      <th className="text-left p-2">Akcje</th>
                    </tr>
                  </thead>
                  <tbody>
                    {refurbishCandidates.map((r) => (
                      <tr key={r.id} className="border-t">
                        <td className="p-2">
                          <Checkbox checked={selectedIds.has(r.id)} onCheckedChange={() => toggleSelect(r.id)} />
                        </td>
                        <td className="p-2 font-medium">{r.title}</td>
                        <td className="p-2">
                          {r.wp_post_url && (
                            <a href={r.wp_post_url} target="_blank" rel="noreferrer" className="text-primary hover:underline flex items-center gap-1">
                              {r.wp_post_title}
                              <ExternalLink className="h-3 w-3" />
                            </a>
                          )}
                        </td>
                        <td className="p-2">
                          <QualityScoreBadge
                            score={r.existing_wp_quality_score}
                            recommendation={r.wp_post_quality_recommendation}
                          />
                        </td>
                        <td className="p-2">
                          <div className="flex flex-wrap gap-1 max-w-xs">
                            {(r.wp_post_quality_red_flags || []).slice(0, 3).map((f) => (
                              <Badge key={f} variant="destructive" className="text-[10px]">
                                {QUALITY_FLAGS_LABELS[f] || f}
                              </Badge>
                            ))}
                          </div>
                        </td>
                        <td className="p-2">
                          {r.refurbish_approved_by_user ? (
                            <Badge className="bg-green-500/15 text-green-700">Zatwierdzono</Badge>
                          ) : (
                            <Badge variant="outline">Czeka</Badge>
                          )}
                        </td>
                        <td className="p-2">
                          <div className="flex gap-1">
                            {!r.refurbish_approved_by_user && (
                              <Button
                                size="sm"
                                variant="outline"
                                onClick={() =>
                                  approveRefurbish.mutate({
                                    nodeId: r.id,
                                    userId: user?.id ?? null,
                                  })
                                }
                              >
                                Zatwierdź
                              </Button>
                            )}
                            <Button
                              size="sm"
                              variant="ghost"
                              onClick={() =>
                                changeStatus.mutate({
                                  nodeId: r.id,
                                  status: "matched_keep",
                                  reason: "user override → keep",
                                })
                              }
                            >
                              Zostaw
                            </Button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </>
          </CardContent>
        </Card>
      )}

      {/* Section 6: Full results table with filters */}
      <Card>
        <CardHeader>
          <CardTitle>Wyniki matchingu — wszystkie nody</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex gap-3 mb-4">
            <Select value={statusFilter} onValueChange={setStatusFilter}>
              <SelectTrigger className="w-56">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Wszystkie statusy</SelectItem>
                <SelectItem value="unchecked">Do sprawdzenia</SelectItem>
                <SelectItem value="unmatched">Do generowania</SelectItem>
                <SelectItem value="matched_keep">Zachowaj</SelectItem>
                <SelectItem value="matched_refurbish">Do przebudowy</SelectItem>
                <SelectItem value="partial">Częściowe</SelectItem>
                <SelectItem value="skip_manual">Pomiń ręcznie</SelectItem>
              </SelectContent>
            </Select>
            <Input
              placeholder="Szukaj po tytule..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="max-w-sm"
            />
          </div>
          <div className="rounded border bg-background max-h-[500px] overflow-y-auto">
            <table className="w-full text-sm">
              <thead className="bg-muted/50 sticky top-0">
                <tr>
                  <th className="text-left p-2">Node</th>
                  <th className="text-left p-2">Status</th>
                  <th className="text-left p-2">Confidence</th>
                  <th className="text-left p-2">Wpis WP</th>
                  <th className="text-left p-2">Jakość</th>
                </tr>
              </thead>
              <tbody>
                {filteredRows.map((r: MatchRow) => (
                  <tr key={r.id} className="border-t">
                    <td className="p-2 font-medium">{r.title}</td>
                    <td className="p-2"><MatchStatusBadge status={r.match_status} /></td>
                    <td className="p-2">{r.match_confidence != null ? `${(r.match_confidence * 100).toFixed(0)}%` : "—"}</td>
                    <td className="p-2">
                      {r.wp_post_url ? (
                        <a href={r.wp_post_url} target="_blank" rel="noreferrer" className="text-primary hover:underline">
                          {r.wp_post_title || "wpis"}
                        </a>
                      ) : "—"}
                    </td>
                    <td className="p-2">
                      <QualityScoreBadge score={r.existing_wp_quality_score} recommendation={r.wp_post_quality_recommendation} />
                    </td>
                  </tr>
                ))}
                {filteredRows.length === 0 && (
                  <tr>
                    <td colSpan={5} className="p-6 text-center text-sm text-muted-foreground">
                      Brak wyników — uruchom matching.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>

      {/* Section 7: Refurbish history (collapsible — defaultowo zwinięta) */}
      <Collapsible defaultOpen={false}>
        <Card>
          <CollapsibleTrigger className="w-full text-left">
            <CardHeader className="hover:bg-muted/30 transition-colors cursor-pointer">
              <CardTitle className="flex items-center justify-between text-base">
                <span>
                  Historia przebudowy{" "}
                  {history.data && history.data.length > 0 && (
                    <Badge variant="secondary" className="ml-2 text-[10px]">
                      {history.data.length}
                    </Badge>
                  )}
                </span>
                <ChevronDown className="h-4 w-4 text-muted-foreground transition-transform data-[state=open]:rotate-180" />
              </CardTitle>
            </CardHeader>
          </CollapsibleTrigger>
          <CollapsibleContent>
            <CardContent>
              {!history.data || history.data.length === 0 ? (
                <p className="text-sm text-muted-foreground">Brak historii przebudowy.</p>
              ) : (
                <div className="rounded border bg-background">
                  <table className="w-full text-sm">
                    <thead className="bg-muted/50">
                      <tr>
                        <th className="text-left p-2">Data</th>
                        <th className="text-left p-2">Stary tytuł</th>
                        <th className="text-left p-2">Nowy tytuł</th>
                        <th className="text-left p-2">URL</th>
                        <th className="text-left p-2">Stan</th>
                        <th className="text-left p-2">Akcje</th>
                      </tr>
                    </thead>
                    <tbody>
                      {history.data.map((e) => (
                        <tr key={e.id} className="border-t">
                          <td className="p-2 text-xs">
                            {new Date(e.backup_at).toLocaleString("pl-PL")}
                          </td>
                          <td className="p-2">{e.old_title}</td>
                          <td className="p-2">{e.new_title || "—"}</td>
                          <td className="p-2">
                            {e.wp_post_url && (
                              <a
                                href={e.wp_post_url}
                                target="_blank"
                                rel="noreferrer"
                                className="text-primary hover:underline"
                              >
                                {e.wp_post_url}
                              </a>
                            )}
                          </td>
                          <td className="p-2">
                            {e.restored_at ? (
                              <Badge variant="secondary">Przywrócono</Badge>
                            ) : (
                              <Badge>Aktywna nowa</Badge>
                            )}
                          </td>
                          <td className="p-2">
                            {!e.restored_at && (
                              <Button
                                size="sm"
                                variant="ghost"
                                onClick={() => setRollbackTarget(e.id)}
                              >
                                <RotateCcw className="h-3 w-3 mr-1" />
                                Przywróć
                              </Button>
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </CardContent>
          </CollapsibleContent>
        </Card>
      </Collapsible>

        </TabsContent>
        )}
        {/* STOP-2: end goalMode='full_audit' TabsContent step-2 wrapper */}
        {/* ══════════════════════════════════════════════════════
            TAB SUMMARY — WERYFIKACJA (read-only dashboard + Eksport + Danger zone)
            ══════════════════════════════════════════════════════ */}
        <TabsContent value="summary" className="space-y-4 mt-4">
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-base">Weryfikacja — podsumowanie i eksport</CardTitle>
              <CardDescription>
                Read-only dashboard wszystkich zebranych danych: Pipeline Progress (6 etapów),
                4 karty wymiarów (Jakość/Scope/SEO/Decyzje), Matrix Scope×Jakość, Matching stats,
                Action logs, Kosz, Eksport JSON. Opcjonalna zakładka — informacyjna.
              </CardDescription>
            </CardHeader>
          </Card>

          {map.website_id && (
            <PanelSummary
              posts={wpPosts.data?.posts || []}
              websiteId={map.website_id}
              wpPostsSyncedAt={map.wp_posts_synced_at ?? null}
              onExportJson={handleExportJson}
              isExporting={exportJson.isPending}
              mapId={mapId}
              onOpenFullAudit={() => {
                // Skok do kroku "audit" w pipeline steppera (via URL query param)
                const url = new URL(window.location.href);
                url.searchParams.set("step", "audit");
                window.location.href = url.toString();
              }}
            />
          )}

          {/* Danger zone — wyczyść wpisy z bazy + reset stanu (jeden przycisk = jedna operacja) */}
          <Card className="border-red-500/50 bg-red-50/30 dark:bg-red-950/10">
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-red-700 dark:text-red-400">
                <AlertTriangle className="h-5 w-5" />
                ⚠ Danger zone — Wyczyść wpisy z bazy
              </CardTitle>
              <CardDescription>
                Usuwa wszystkie pobrane wpisy z lokalnej bazy dla tej domeny (<code>wp_posts</code>) +
                zrywa matche nodów + resetuje stan sync mapy. Dobre gdy chcesz zacząć analizę od zera
                (np. domena ma słabe wpisy i wolisz analizować mapę bez ich wpływu).
                <br />
                <strong className="text-emerald-600 dark:text-emerald-400">
                  ✓ NIE kasuje wpisów na WordPressie
                </strong>{" "}
                ·{" "}
                <strong className="text-emerald-600 dark:text-emerald-400">
                  ✓ NIE kasuje nodów ani strategii mapy
                </strong>
              </CardDescription>
            </CardHeader>
            <CardContent>
              <Button
                variant="destructive"
                onClick={handleOpenReset}
                disabled={resetDebug.isPending || !map.website_id}
              >
                <Trash2 className="h-4 w-4 mr-2" />
                {resetDebug.isPending
                  ? "Czyszczenie..."
                  : "🗑 Wyczyść wpisy z bazy + reset matching"}
              </Button>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* AlertDialog: Pre-flight warning przy braku matchera (defense-in-depth — przycisk
          Klasyfikuj scope jest LOCKED w Tab 1 Krok 4 gdy matchedCount === 0, ale dialog
          dalej istnieje na wypadek programowego wywołania handleClassifyClusterFit np. z
          Header CTA). */}
      <AlertDialog open={showClusterFitWarning} onOpenChange={setShowClusterFitWarning}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Matcher nie był odpalony</AlertDialogTitle>
            <AlertDialogDescription>
              Bez matchera wszystkie wpisy pójdą przez LLM (~4x droższe niż z
              matcherem — auto-matched shortcut nie zadziała). Zalecamy najpierw
              odpalić <strong>Matching</strong> (Tab 1 → Krok 3).
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel onClick={() => setShowClusterFitWarning(false)}>
              Anuluj
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={() => {
                setShowClusterFitWarning(false);
                runClusterFit();
              }}
            >
              Klasyfikuj i tak
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Reset/clear wp_posts confirm dialog */}
      <AlertDialog open={showResetDialog} onOpenChange={setShowResetDialog}>
        <AlertDialogContent className="max-w-xl">
          <AlertDialogHeader>
            <AlertDialogTitle>Wyczyść wpisy z bazy dla tej domeny</AlertDialogTitle>
            <AlertDialogDescription asChild>
              <div className="space-y-3 text-sm">
                <p>
                  Usuwa wszystkie pobrane wpisy z lokalnej bazy dla domeny{" "}
                  <strong>{map.website?.name || ""}</strong> i zrywa matche nodów tej mapy.
                  Przydatne gdy wpisy domeny są słabej jakości i wolisz analizować mapę bez ich wpływu.
                </p>

                <div>
                  <p className="font-semibold text-red-700 dark:text-red-400 mb-1">ZOSTANĄ USUNIĘTE:</p>
                  <ul className="list-none space-y-1">
                    <li>✓ Matche wszystkich nodów tej mapy (powrót do 'unchecked')</li>
                    <li>✓ Stan sync tej mapy (wp_posts_synced_at, wp_posts_count)</li>
                    <li>✓ Historia matching runs dla tej mapy</li>
                    <li>
                      ✓ WSZYSTKIE POBRANE WPISY z bazy dla domeny <strong>{map.website?.name || ""}</strong>{" "}
                      (następny sync pobierze je ponownie z WordPressa)
                    </li>
                  </ul>
                </div>

                {otherMapsCount > 0 && (
                  <div className="flex gap-2 p-3 border border-amber-500/50 bg-amber-50/60 dark:bg-amber-950/20 rounded text-amber-800 dark:text-amber-300">
                    <AlertTriangle className="h-4 w-4 shrink-0 mt-0.5" />
                    <p>
                      Ta domena jest używana też przez <strong>{otherMapsCount}</strong> innych map. One też
                      stracą pobrane wpisy (ale ich matche ZOSTANĄ — ten reset dotyczy tylko tej mapy).
                    </p>
                  </div>
                )}

                <div>
                  <p className="font-semibold text-green-700 dark:text-green-400 mb-1">NIE ZOSTANĄ USUNIĘTE:</p>
                  <ul className="list-none space-y-1">
                    <li>✗ Nody mapy (tylko ich pola match_* będą wyczyszczone)</li>
                    <li>✗ Wpisy na WordPressie (kasujemy tylko lokalną kopię)</li>
                    <li>✗ Historia przebudowy (wp_post_refurbish_backups)</li>
                  </ul>
                </div>
              </div>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={resetDebug.isPending}>Anuluj</AlertDialogCancel>
            <AlertDialogAction
              onClick={(e) => {
                e.preventDefault();
                handleReset();
              }}
              className="bg-red-600 hover:bg-red-700 text-white"
              disabled={resetDebug.isPending}
            >
              {resetDebug.isPending ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Czyszczenie...
                </>
              ) : (
                <>
                  <Trash2 className="h-4 w-4 mr-2" />
                  Tak, wyczyść
                </>
              )}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Bulk approve confirm dialog */}
      <AlertDialog open={showBulkConfirm} onOpenChange={setShowBulkConfirm}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Zatwierdzasz przebudowę {selectedIds.size} wpisów</AlertDialogTitle>
            <AlertDialogDescription>
              Po uruchomieniu Content Engine:
              <ul className="list-disc ml-6 mt-2 space-y-1">
                <li>Nowa treść ZASTĄPI istniejące wpisy na zapleczu.</li>
                <li>URL i slug pozostaną takie same (ważne dla SEO).</li>
                <li>Stare wersje trafiają do backup table — rollback możliwy.</li>
                <li>WordPress zachowuje też revisions.</li>
              </ul>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Anuluj</AlertDialogCancel>
            <AlertDialogAction onClick={handleBulkApprove}>Zatwierdź</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Rollback confirm */}
      {/* Floating activity log — fixed bottom-right, persistent przez wszystkie taby.
          Auto-expand gdy nowa operacja startuje (isAnyPending), minimize do paska
          z localStorage persist. Zastępuje stary inline logs panel. */}
      <FloatingActivityLog
        logs={logs}
        isAnyPending={isAnyPending}
        latestRunId={latestRunId}
        isDebugCollectorActive={isLogCollectorActive()}
        onClear={() => setLogs([])}
        onDownloadDebugRun={(runId) => downloadLogRunAsTxt(runId)}
      />

      <AlertDialog open={!!rollbackTarget} onOpenChange={(o) => !o && setRollbackTarget(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Przywróć starą wersję wpisu?</AlertDialogTitle>
            <AlertDialogDescription>
              Nadpisze aktualną treść na WordPressie starą wersją z backupu. URL pozostanie ten sam.
              Nowa wersja nie będzie automatycznie przywracalna z tego panelu (ale WP zachowuje revisions).
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Anuluj</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => rollbackTarget && handleRollback(rollbackTarget)}
            >
              Przywróć
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
